import {
  CUSTOM_FIELD_CONDITION_OPERATORS,
  operatorCarriesValue,
} from '@payaca/gql-helpers';
import { z } from 'zod/v4';

import { ComplexConditionDef } from '../../../types/complexConditionDef';
import { PipelineGuardCustomFieldCondition } from '../../../types/pipelineGuardTypes';
import CustomFieldConditionExtraPicker, {
  CustomFieldConditionExtra,
} from './CustomFieldConditionExtraPicker';

const operatorSchema = z.union([
  z.literal(''),
  z.enum([...CUSTOM_FIELD_CONDITION_OPERATORS]),
]);

const customFieldConditionExtraSchema: z.ZodType<CustomFieldConditionExtra> = z
  .object({
    fieldPath: z
      .string({ error: 'A custom field must be selected' })
      .min(1, 'A custom field must be selected'),
    operator: operatorSchema,
    comparisonValue: z.unknown().optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.operator) {
      ctx.addIssue({
        code: 'custom',
        message: 'A condition must be selected',
        path: ['operator'],
      });
    }
    const valueRequired =
      data.operator !== '' && operatorCarriesValue(data.operator);
    if (
      valueRequired &&
      (data.comparisonValue === undefined ||
        data.comparisonValue === null ||
        data.comparisonValue === '')
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'A value must be entered',
        path: ['comparisonValue'],
      });
    }
  });

const customFieldConditionDef: ComplexConditionDef<
  PipelineGuardCustomFieldCondition,
  CustomFieldConditionExtra
> = {
  key: 'CUSTOM_FIELD_CONDITION',
  label: (t) => t('pipelineGuards.conditions.CUSTOM_FIELD_CONDITION.entry'),
  // No `inverseLabel`: every shipped operator pairs with a sibling that
  // expresses the negation directly (`equals`↔`notEquals`,
  // `isSet`↔`isNotSet`, etc.), so an inverse selector entry would be
  // redundant. `GuardConditionRow` filters defs without `inverseLabel`
  // out of the inverse list.
  extraSchema: customFieldConditionExtraSchema,
  matches: (child): child is PipelineGuardCustomFieldCondition =>
    child.type === 'customFieldCondition',
  deriveExtra: (child) => ({
    fieldPath: child.fieldPath,
    operator: child.operator,
    comparisonValue: child.comparisonValue,
  }),
  toChild: (extra, tempIdentifier) => {
    if (!extra.operator) {
      // `extraSchema` rejects an empty operator before `toChild` runs;
      // throw rather than silently constructing an ill-typed guard.
      throw new Error(
        'customFieldConditionDef.toChild called with unselected operator'
      );
    }
    if (extra.operator === 'equals' || extra.operator === 'notEquals') {
      return {
        type: 'customFieldCondition',
        operator: extra.operator,
        fieldPath: extra.fieldPath,
        comparisonValue: extra.comparisonValue,
        tempIdentifier,
      };
    }
    return {
      type: 'customFieldCondition',
      operator: extra.operator,
      fieldPath: extra.fieldPath,
      tempIdentifier,
    };
  },
  ExtraPicker: CustomFieldConditionExtraPicker,
};

export default customFieldConditionDef;
